/**
 * /dashboard/organizations (screenshots/56–59; brief §2.10). Tabs: Clients · Lobbyists · Following ·
 * Coalitions (advocacy). Paints from snapshot:organizations (orgStore); "To alderpersons" always counts the
 * client, its employees and its registered lobbyists (the public client pages' counting).
 */
import { useMemo, useRef, useState } from "react";
import { BadgeDollarSign, Briefcase, Building2, Download, FileText, Info, Plus, Search, StickyNote, Tag, UserPlus, X } from "lucide-react";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { List, ListRow, RowBelow, RowCell, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { NoResults } from "../components/shared/States";
import { Kbd, Switch, TabBar, ViewSwitch } from "../components/shared/ui";
import { useListKeyboard } from "../hooks/useTabs";
import { navigate } from "../app/router";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { andList, amountFor, monthDay, sortClients, usd, usdShort, yourIssue, type ClientSort } from "./logic";
import { initials } from "./build";
import { orgStore, useOrgs } from "./orgStore";
import type { ClientSummary, CoalitionEntry, LobbyistSummary, OrgListData } from "./types";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useDismiss } from "../hooks/useDismiss";

export type OrgListTab = "clients" | "lobbyists" | "following" | "coalitions";
export const ORG_LIST_TABS: OrgListTab[] = ["clients", "lobbyists", "following", "coalitions"];
export type { OrgListData };

const open = (id: string) => navigate(`/dashboard/organizations/${encodeURIComponent(id)}`);
const Logo = ({ name, size = "sm" }: { name: string; size?: "sm" | "md" }) => <span className={`og-logo og-logo--${size}`} aria-hidden>{initials(name)}</span>;
const Initials = ({ name }: { name: string }) => <span className="og-initials" role="img" aria-label={name}>{name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join("")}</span>;

export function OrganizationsList({ group, role, tab, onTab }: { group: UserGroup; role: OrgRole | null; tab: OrgListTab; onTab: (t: OrgListTab) => void }) {
  const data = useOrgs((s) => s.list);
  if (!data) return <p className="empty-panel">Organizations aren't available yet.</p>;
  const coalitions = can(group, role, "coalitions");
  const active = tab === "coalitions" && !coalitions ? "clients" : tab;
  const tabs = [
    { id: "clients", label: "Clients", ...(active !== "clients" ? { count: data.totals.clients } : {}) },
    { id: "lobbyists", label: "Lobbyists", ...(active !== "lobbyists" ? { count: data.totals.lobbyists } : {}) },
    { id: "following", label: "Following", ...(active !== "following" ? { count: data.followed.length } : {}) },
    ...(coalitions ? [{ id: "coalitions", label: "Coalitions", ...(active !== "coalitions" ? { count: data.coalitions.filter((c) => c.status === "confirmed").length } : {}) }] : []),
  ] as { id: OrgListTab; label: string; count?: number }[];
  return (
    <div className="page og-page">
      <header className="page-header og-head">
        <div className="page-header__title">
          <div className="title-row"><h1 className="page-title">Organizations</h1><ConnectionStatus /></div>
        </div>
        <div className="row-gap og-head-actions">
          <HowMatched />
          {can(group, role, "csvExport") && <a className="btn btn--secondary og-head-btn" href={`/api/dashboard/orgs/export.csv?tab=${active === "lobbyists" ? "lobbyists" : "clients"}`} download><Download size={14} aria-hidden /><span className="truncate">Export CSV</span></a>}
        </div>
      </header>
      <div className="signal-strip">
        {([[Building2, "Clients", data.totals.clients.toLocaleString("en-US"), "registered with the Board of Ethics"],
          [Briefcase, "Lobbyists", data.totals.lobbyists.toLocaleString("en-US"), `active since ${data.totals.since}`],
          [FileText, "Lobbying filings", data.totals.filings.toLocaleString("en-US"), `since ${data.totals.since}`],
          [BadgeDollarSign, "To alderpersons", usdShort(data.totals.toAlderpersons), `${usd(data.totals.toAlderpersons)} since ${data.totals.since}`]] as const).map(([Icon, l, v, s]) => (
          <div key={l} className="signal-tile signal-tile--static"><span className="signal-tile__label"><Icon size={14} aria-hidden />{l}</span>
            <span className="signal-tile__value"><b>{v}</b><span className="signal-tile__extra">{s}</span></span></div>
        ))}
      </div>
      <TabBar label="Organizations" tabs={tabs} active={active} onChange={onTab} />
      <div className="tab-panel" key={active} role="tabpanel">
        {active === "clients" && <Clients data={data} />}
        {active === "lobbyists" && <Lobbyists data={data} />}
        {active === "following" && <Following data={data} />}
        {active === "coalitions" && <Coalitions entries={data.coalitions} clients={data.clients} />}
      </div>
    </div>
  );
}

/** "How money is matched": the matching and overlap notes from the design, in one popover (not designed as a page). */
function HowMatched() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useDismiss(ref, open, () => setOpen(false));
  return (
    <span className="og-how" ref={ref}>
      <button className="btn btn--secondary og-head-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}><Info size={14} aria-hidden /><span className="truncate">How money is matched</span></button>
      {open && (
        <div className="og-pop__card og-how__card" role="dialog" aria-label="How money is matched">
          <p>"To alderpersons" includes money from the client, its employees and its registered lobbyists.</p>
          <p>A lobbyist's own contributions are shown under every client they represent, so client totals overlap.</p>
          <p>Money near a vote isn’t evidence of influence. Sources: ISBE and Board of Ethics, matched by name.</p>
        </div>
      )}
    </span>
  );
}

function SearchBox({ placeholder, value, onChange }: { placeholder: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="og-search"><Search size={15} aria-hidden />
      <input type="search" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} />
      <Kbd>⌘K</Kbd>
    </label>
  );
}

/** Clients tab (screenshots/56). */
function Clients({ data }: { data: OrgListData }) {
  const [sort, setSort] = useState<ClientSort>("legislation");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(10);
  const list = useRef<HTMLDivElement>(null);
  useListKeyboard(list, { open });
  const matches = useMemo(() => {
    const n = q.trim().toLowerCase();
    return sortClients(n ? data.clients.filter((c) => c.name.toLowerCase().includes(n)) : data.clients, sort);
  }, [data.clients, sort, q]);
  const rows = matches.slice(0, shown);
  const onIssues = useMemo(() => [...data.clients].filter((c) => yourIssue(c, data.followedTopics)).sort((a, b) => b.legislationCount - a.legislationCount || b.filings - a.filings).slice(0, 3), [data]);
  const topMoney = sortClients(rows, "money").slice(0, 3);
  const max = Math.max(1, ...topMoney.map((c) => amountFor(c.money, "all")));
  return (
    <div className="cols og-cols">
      <div className="cols__main og-main">
        <div className="og-toolbar">
          <SearchBox placeholder={`Search ${data.totals.clients} clients`} value={q} onChange={(v) => { setQ(v); setShown(10); }} />
          <ViewSwitch label="Sort" value={sort} onChange={setSort} options={[{ id: "legislation", label: "Most legislation" }, { id: "filings", label: "Most filings" }, { id: "money", label: "Most to alderpersons" }, { id: "az", label: "A–Z" }]} />
        </div>
        <div ref={list}>
          <table className="og-table og-table--clients">
            <thead><tr><th>Client</th><th className="num">Legislation</th><th className="num">Filings</th><th className="num">To alderpersons</th><th>Your issues</th></tr></thead>
            <tbody>{rows.map((c) => {
              const issue = yourIssue(c, data.followedTopics);
              const money = amountFor(c.money, "all");
              return (
                <tr key={c.id} data-row={c.id} tabIndex={0} onClick={() => open(c.id)} onKeyDown={(e) => { if (e.key === "Enter") open(c.id); }}>
                  <td><span className="og-who"><Logo name={c.name} /><span><b>{c.name}</b>{c.kind && <small>{c.kind}</small>}</span></span></td>
                  <td className="num">{c.legislationCount}</td>
                  <td className="num og-muted">{c.filings}</td>
                  <td className="num">{money ? <b>{usd(money)}</b> : <span className="og-dash">—</span>}</td>
                  <td>{issue ? <span className="og-topic"><Tag size={13} aria-hidden />{issue}</span> : <span className="og-dash">—</span>}</td>
                </tr>
              );
            })}</tbody>
          </table>
          {q.trim() && rows.length === 0 && <NoResults query={q.trim()} />}
        </div>
        <div className="og-foot">
          <span>Showing {rows.length} of {matches.length}</span>
          {matches.length > rows.length && <button className="link-arrow" onClick={() => setShown((n) => n + 25)}>Show {Math.min(25, matches.length - rows.length)} more<ArrowRight /></button>}
        </div>
      </div>
      <aside className="cols__side">
        {onIssues.length > 0 && (
          <section className="og-card">
            <h3>Active on your issues</h3>
            <List className="og-list">{onIssues.map((c) => (
              <ListRow key={c.id} className="og-issue" href={`/dashboard/organizations/${c.id}`}>
                <RowMain min={120}><RowTitle as="b" lines={2}>{c.name}</RowTitle><RowSub>{c.legislationCount} items · {c.filings} filings</RowSub></RowMain>
                <RowTrail><span className="og-chip">Unclear position</span></RowTrail>
              </ListRow>))}</List>
          </section>
        )}
        <section className="og-card">
          <h3>Most to alderpersons in this list</h3>
          <List variant="plain" className="og-bars">{topMoney.filter((c) => amountFor(c.money, "all") > 0).map((c) => { const v = amountFor(c.money, "all"); return (
            <ListRow key={c.id} className="og-bar-row">
              <RowMain min={80}><RowTitle>{c.name}</RowTitle></RowMain>
              <RowTrail><b>{usd(v)}</b></RowTrail>
              <RowBelow><span className="og-track"><span style={{ width: `${(v / max) * 100}%` }} /></span></RowBelow>
            </ListRow>); })}</List>
          <p className="og-caveat">Money near a vote isn’t evidence of influence.</p>
        </section>
      </aside>
    </div>
  );
}

const ArrowRight = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>;

/** Lobbyists tab (screenshots/57). */
function Lobbyists({ data }: { data: OrgListData }) {
  const [sort, setSort] = useState<"filings" | "money">("filings");
  const [q, setQ] = useState("");
  const pinned = useMemo(() => new Set(data.lobbyistsOnYourIssues.slice(0, 1)), [data.lobbyistsOnYourIssues]);
  const byId = useMemo(() => new Map(data.lobbyists.map((l) => [l.id, l])), [data.lobbyists]);
  const { rows, total } = useMemo(() => {
    const n = q.trim().toLowerCase();
    const all = (n ? data.lobbyists.filter((l) => l.name.toLowerCase().includes(n) || l.firm.toLowerCase().includes(n)) : data.lobbyists)
      .slice().sort((a, b) => (sort === "filings" ? b.filings - a.filings : b.toAlderpersons - a.toAlderpersons));
    const pins = n ? [] : all.filter((l) => pinned.has(l.id));
    return { rows: [...all.filter((l) => !pins.includes(l)).slice(0, 8 - pins.length), ...pins], total: all.length };
  }, [data.lobbyists, sort, q, pinned]);
  const pin = rows.find((l) => pinned.has(l.id));
  const sub = (l: LobbyistSummary) => {
    if (l.clients <= 1) return null;
    const names = (l.clientNames ?? []).slice();
    const issue = data.followed.map((f) => f.name.toLowerCase());
    names.sort((a, b) => (issue.includes(b.toLowerCase()) ? 1 : 0) - (issue.includes(a.toLowerCase()) ? 1 : 0));
    const pick = [...names.filter((n) => issue.includes(n.toLowerCase()))].slice(0, 2);
    return `${l.clients} clients${pick.length ? `, including ${andList(pick)}` : ""}`;
  };
  const side = data.lobbyistsOnYourIssues.map((id) => byId.get(id)).filter((l): l is LobbyistSummary => !!l);
  const kasper = side.find((l) => l.clients > 1);
  return (
    <div className="cols og-cols">
      <div className="cols__main og-main">
        <div className="og-toolbar">
          <SearchBox placeholder={`Search ${data.totals.lobbyists} lobbyists or firms`} value={q} onChange={setQ} />
          <ViewSwitch label="Sort" value={sort} onChange={setSort} options={[{ id: "filings", label: "Most filings" }, { id: "money", label: "Most to alderpersons" }]} />
        </div>
        <table className="og-table og-table--lobbyists">
          <thead><tr><th>Lobbyist</th><th>Firm</th><th className="num">Filings</th><th className="num">To alderpersons</th></tr></thead>
          <tbody>{rows.map((l) => { const s = sub(l); return (
            <tr key={l.id} data-pinned={pinned.has(l.id) || undefined}>
              <td><span className="og-who"><Initials name={l.name} /><span><b>{l.name}</b>{s && <small>{s}</small>}</span></span></td>
              <td className="og-firm">{l.firm}</td>
              <td className="num">{l.filings.toLocaleString("en-US")}</td>
              <td className="num">{l.toAlderpersons ? <b>{usd(l.toAlderpersons)}</b> : <span className="og-dash">—</span>}</td>
            </tr>); })}</tbody>
        </table>
        {q.trim() && rows.length === 0 && <NoResults query={q.trim()} />}
        <div className="og-foot"><span>Showing {rows.length} of {total} · sorted by {sort === "filings" ? "filings" : "money to alderpersons"}{pin ? `, with ${pin.name} pinned because they lobby on your issues` : ""}</span></div>
      </div>
      <aside className="cols__side">
        {side.length > 0 && (
          <section className="og-card">
            <h3>Lobbyists on your issues</h3>
            <List className="og-list">{side.map((l) => (
              <ListRow key={l.id} className="og-person">
                <RowLead><Initials name={l.name} /></RowLead>
                <RowMain min={120}><RowTitle as="b">{l.name}</RowTitle>
                  <RowSub wrap>{l.clients > 1 ? `${l.clients} clients · ${usd(l.toAlderpersons) === "—" ? "$0" : usd(l.toAlderpersons)} to ${l.alderpersonsFunded} alderpersons` : `1 client · ${l.filings} filings · ${l.toAlderpersons ? usd(l.toAlderpersons) : "$0"}`}</RowSub></RowMain>
              </ListRow>))}</List>
          </section>
        )}
        <section className="og-note">
          <h3>How lobbyist money is counted</h3>
          <p>A lobbyist's own contributions are shown under every client they represent.{kasper ? ` ${kasper.name}'s ${usd(kasper.toAlderpersons)} appears under ${andOthers(kasper, data)}, so client totals overlap.` : " So client totals overlap."}</p>
        </section>
      </aside>
    </div>
  );
}

/** "Chicagoland Apartment Association and his 61 other clients" → neutral wording: "… and 61 other clients". */
function andOthers(l: LobbyistSummary, data: OrgListData) {
  const here = data.followed.find((f) => (l.clientNames ?? []).some((n) => n.toLowerCase() === f.name.toLowerCase()))?.name ?? l.clientNames?.[0];
  return here ? `${here} and their ${l.clients - 1} other clients` : `${l.clients} clients`;
}

/** Following tab (screenshots/58). */
function Following({ data }: { data: OrgListData }) {
  const alerts = useOrgs((s) => s.list!.alerts);
  return (
    <div className="cols og-cols">
      <div className="cols__main og-main">
        {data.catchUp.lines.length > 0 && (
          <section className="og-catchup">
            <h3>Since your last visit{data.catchUp.since ? ` · ${monthDay(data.catchUp.since)}` : ""}</h3>
            {data.catchUp.lines.map((l, i) => <p key={i}>{l.icon === "filing" ? <FileText size={15} aria-hidden /> : <BadgeDollarSign size={15} aria-hidden />}{l.text}</p>)}
          </section>
        )}
        {data.followed.length > 0 && (
          <div className="og-cards">{data.followed.map((c) => {
            const chip = c.basis === "stated" && c.position ? `${c.position === "oppose" ? "Opposes" : "Supports"}${c.subject ? ` ${c.subject}` : ""}` : "Position unclear";
            const lobShare = c.money.lobbyists && c.money.lobbyists / Math.max(1, c.money.company + c.money.employees + c.money.lobbyists) > 0.9;
            return (
              <a key={c.id} className="og-org-card" href={`/dashboard/organizations/${c.id}`} onClick={(e) => { e.preventDefault(); open(c.id); }}>
                <header><Logo name={c.name} size="md" /><span className="truncate"><b>{c.name}</b>
                  <small>{[c.kind, lobShare ? `${usd(c.money.lobbyists)} of it from its ${c.lobbyistCount ?? ""} lobbyists`.replace("  ", " ") : ""].filter(Boolean).join(" · ")}</small></span></header>
                <div className="og-org-card__stats">
                  <span><b>{c.filings}</b><small>filings</small></span><span><b>{c.legislationCount}</b><small>items</small></span>
                  <span><b>{usd(amountFor(c.money, "all"))}</b><small>to alderpersons</small></span>
                </div>
                <footer><span className={`og-chip${c.basis === "stated" && c.position === "oppose" ? " og-chip--negative" : c.basis === "stated" ? " og-chip--positive" : ""}`}>{chip}</span><span className="og-org-card__latest">{c.latest}</span></footer>
              </a>);
          })}</div>
        )}
      </div>
      <aside className="cols__side">
        <section className="og-card">
          <h3>Alerts for organizations you follow</h3>
          <div className="og-switches">
            {([["newFiling", "New lobbying filing"], ["newContribution", "New contribution to a member"], ["lobbiesOnFollowed", "Lobbies on an item you follow"], ["weekly", "Weekly summary"]] as const).map(([k, l]) => (
              <div key={k} className="og-switch-row"><span>{l}</span><Switch on={alerts[k]} label={l} onChange={(v) => void orgStore.getState().setAlert(k, v)} /></div>))}
          </div>
        </section>
      </aside>
    </div>
  );
}

/** Coalitions tab (screenshots/59; advocacy only). */
function Coalitions({ entries, clients }: { entries: CoalitionEntry[]; clients: ClientSummary[] }) {
  const [adding, setAdding] = useState<{ clientId?: string } | null>(null);
  const sec = (status: CoalitionEntry["status"], title: string) => {
    const list = entries.filter((e) => e.status === status);
    if (!list.length) return null;
    return (
      <section className="og-coalition">
        <div className="og-group-label"><span>{title}</span><span className="count-badge">{list.length}</span></div>
        <List className="og-coalition-list">{list.map((e) => (
          <ListRow key={e.clientId} className="og-coalition-row">
            <RowLead><Logo name={e.name} size="md" /></RowLead>
            <RowMain min={140} className="og-coalition-row__name"><RowTitle as="b">{e.name}</RowTitle><RowSub>{e.sub}</RowSub></RowMain>
            <RowCell width={170}>{e.status === "confirmed"
              ? <span className="og-chip og-chip--positive">{e.match ? `${e.match.same} of ${e.match.of} match` : "Same side"}</span>
              : <span className="og-chip">Position unclear</span>}</RowCell>
            <RowCell width={98} align="end">{e.status === "confirmed"
              ? <a className="btn btn--secondary og-row-btn" href={`/dashboard/organizations/${e.clientId}?tab=lobbyists`}><UserPlus size={14} aria-hidden />Reach out</a>
              : <button className="btn btn--secondary og-row-btn" onClick={() => setAdding({ clientId: e.clientId })}><StickyNote size={14} aria-hidden />Add note</button>}</RowCell>
          </ListRow>
        ))}</List>
      </section>
    );
  };
  return (
    <div className="cols og-cols">
      <div className="cols__main og-main og-main--tight">
        {sec("confirmed", "Confirmed on your side")}
        {sec("possible", "Possible allies from filings")}
      </div>
      <aside className="cols__side">
        <section className="og-card">
          <h3>Shared with your team</h3>
          <p className="og-card__body">Mark an organization as an ally or opponent on a specific item. Only your team sees it, and it feeds the coalition list.</p>
          <button className="btn btn--primary og-block" onClick={() => setAdding({})}><Plus size={15} aria-hidden />Add an organization</button>
        </section>
      </aside>
      {adding && <CoalitionDialog clients={clients} initial={adding.clientId} onClose={() => setAdding(null)} />}
    </div>
  );
}

/** Mark an organization as an ally or opponent (team-only). Not designed beyond the button: kept minimal. */
function CoalitionDialog({ clients, initial, onClose }: { clients: ClientSummary[]; initial?: string; onClose: () => void }) {
  const [q, setQ] = useState(clients.find((c) => c.id === initial)?.name ?? "");
  const [pick, setPick] = useState<ClientSummary | null>(clients.find((c) => c.id === initial) ?? null);
  const [side, setSide] = useState<"ally" | "opponent">("ally");
  const [note, setNote] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const hits = pick ? [] : clients.filter((c) => q.trim() && c.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6);
  return (
    <div className="og-dialog-backdrop" role="presentation" onClick={onClose}>
      <div className="og-dialog" role="dialog" aria-modal="true" aria-label="Add an organization" ref={box} onClick={(e) => e.stopPropagation()}>
        <header><h3>Add an organization</h3><button className="og-icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button></header>
        <label className="og-search"><Search size={15} aria-hidden /><input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} aria-label="Organization" /></label>
        {hits.length > 0 && <div className="og-dialog__hits">{hits.map((c) => <button key={c.id} onClick={() => { setPick(c); setQ(c.name); }}>{c.name}</button>)}</div>}
        <ViewSwitch label="Side" value={side} onChange={setSide} options={[{ id: "ally", label: "Ally" }, { id: "opponent", label: "Opponent" }]} />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} aria-label="Note" />
        <button className="btn btn--primary og-block" disabled={!pick} onClick={async () => {
          if (pick && await orgStore.getState().saveCoalition({ clientId: pick.id, name: pick.name, side, note })) onClose();
        }}>Save</button>
      </div>
    </div>
  );
}
